import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import rushLogo from './rush-logo.png';
import './rush.css';
import './auth.css';
import { AuthMarketingPanel } from './AuthShared';

const StepRail = ({ step }) => (
  <div className="fp-step-rail" aria-label="Reset progress">
    <span className={'step-num ' + (step > 1 ? 'done' : '')}>{step > 1 ? '✓' : '1'}</span>
    <span className={'step-label' + (step === 1 ? '' : ' muted')}>Identify</span>
    <span className="step-bar"></span>
    <span className={'step-num ' + (step === 2 ? '' : step > 2 ? 'done' : 'pending')}>{step > 2 ? '✓' : '2'}</span>
    <span className={'step-label' + (step === 2 ? '' : ' muted')}>Check inbox</span>
    <span className="step-bar"></span>
    <span className={'step-num ' + (step === 3 ? '' : 'pending')}>3</span>
    <span className={'step-label' + (step === 3 ? '' : ' muted')}>New password</span>
  </div>
);

const RushForgotPasswordPage = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const requestReset = async () => {
    await axios.post('/api/users/request-password-reset', { email });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email address.'); return; }
    setSubmitting(true);
    try {
      await requestReset();
      setStep(2);
      setResendCooldown(30);
    } catch (err) {
      // For privacy the backend always returns 200, but handle network errors gracefully
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(30);
    try { await requestReset(); } catch (e) { /* silent — UX-wise we've already shown the cooldown */ }
  };

  return (
    <div className="rs auth-page">
      <section className="auth-form-panel">
        <header className="auth-form-header">
          <Link to="/" className="rs-logo rs-logo-img" aria-label="Rush Studio home">
            <img src={rushLogo} alt="Rush Studio" />
          </Link>
          <div className="auth-form-header-link">Remembered it?<Link to="/login">Sign in</Link></div>
        </header>

        <div className="auth-form-body">
          <Link to="/login" className="fp-back-link">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Back to sign in
          </Link>

          <StepRail step={step} />

          {step === 1 ? (
            <>
              <div className="auth-eyebrow">Account recovery</div>
              <h1 className="auth-title">Forgot your password?</h1>
              <p className="auth-sub">It happens. Enter the email you use for Rush Studio and we'll send you a link to set a new one — valid for 30 minutes.</p>

              {error && (
                <div style={{ padding: '10px 14px', background: 'var(--accent-soft)', color: 'var(--accent-2)', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 14 }} role="alert">{error}</div>
              )}
              <form className="auth-form" onSubmit={submit}>
                <label className="field">
                  <span>Work email</span>
                  <input type="email" className="input input-lg" placeholder="sara@yourcompany.com" required autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <button className="btn btn-accent auth-submit" type="submit" disabled={submitting}>{submitting ? 'Sending…' : 'Send reset link →'}</button>
              </form>

              <div className="fp-alt-block">
                <div className="fp-alt-title">Other ways in</div>
                <div className="fp-alt-list">
                  <Link to="/login" className="fp-alt-item">
                    <span className="fp-alt-item-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12v8H2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="M2 4l6 5 6-5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                    </span>
                    <div className="fp-alt-item-body">
                      <div className="fp-alt-item-title">Sign in with a magic link</div>
                      <div className="fp-alt-item-sub">No password needed — we email you a one-time link.</div>
                    </div>
                    <svg className="fp-alt-item-chev" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </Link>
                  <Link to="/login" className="fp-alt-item">
                    <span className="fp-alt-item-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="3" y="3" width="4" height="4" fill="currentColor" /><rect x="9" y="3" width="4" height="4" fill="currentColor" /><rect x="3" y="9" width="4" height="4" fill="currentColor" /><rect x="9" y="9" width="4" height="4" fill="currentColor" /></svg>
                    </span>
                    <div className="fp-alt-item-body">
                      <div className="fp-alt-item-title">Continue with Google, Microsoft or GitHub</div>
                      <div className="fp-alt-item-sub">If you originally signed up with SSO, use that here.</div>
                    </div>
                    <svg className="fp-alt-item-chev" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </Link>
                  <Link to="/contact" className="fp-alt-item">
                    <span className="fp-alt-item-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.6" /><path d="M8 5v3.2L10 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
                    </span>
                    <div className="fp-alt-item-body">
                      <div className="fp-alt-item-title">Locked out of an SSO workspace?</div>
                      <div className="fp-alt-item-sub">Reach your admin, or contact us — we'll help unblock.</div>
                    </div>
                    <svg className="fp-alt-item-chev" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="auth-eyebrow">Step 2 of 3</div>
              <h1 className="auth-title">Check your inbox.</h1>
              <p className="auth-sub">If an account exists for that email, a reset link is on its way. The link is valid for 30 minutes and can only be used once.</p>

              <div className="fp-sent-card">
                <div className="fp-sent-icon">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 6h16v11H3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M3 6l8 7 8-7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
                </div>
                <div className="fp-sent-body">
                  <h3>Reset link sent</h3>
                  <p>We sent reset instructions to <span className="fp-sent-email">{email || 'you@yourcompany.com'}</span>. Open it on the device you want to sign in on.</p>
                  <div className="fp-sent-meta">
                    <span><b>Expires:</b> in 30 minutes</span>
                    <span><b>From:</b> no-reply@rushstudio.app</span>
                    <span><b>Subject:</b> "Reset your Rush Studio password"</span>
                  </div>
                </div>
              </div>

              <div className="fp-resend-row">
                <span>Didn't get it? Check spam — or try again.</span>
                <button className="resend-btn" onClick={resend} disabled={resendCooldown > 0}>
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend email'}
                </button>
              </div>

              <div className="fp-tips">
                <div className="fp-tips-title">If you don't see it in a few minutes</div>
                <ul>
                  <li>Check your <b>spam, promotions, or "updates"</b> folder. Emails from <code>no-reply@rushstudio.app</code> sometimes land there.</li>
                  <li>Some email providers add a delay — give it up to <b>five minutes</b> before resending.</li>
                  <li>Make sure you entered the email tied to your workspace. Try a different address if needed.</li>
                  <li>Still stuck? <Link to="/contact" style={{ color: 'var(--ink)', fontWeight: 600 }}>Contact support →</Link></li>
                </ul>
              </div>

              <div style={{ textAlign: 'center', marginTop: 18 }}>
                <button type="button" onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--line-2)', textUnderlineOffset: 4 }}>
                  Use a different email
                </button>
              </div>
            </>
          )}

          <div className="auth-aside-link">Need SSO or SAML? <Link to="/contact">Talk to sales →</Link></div>
        </div>

        <footer className="auth-form-footer">
          <span>© {new Date().getFullYear()} Rush Studio Ltd.</span>
          <span><Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link> · <Link to="/contact">Help</Link></span>
        </footer>
      </section>

      <AuthMarketingPanel variant="testimonial" />
    </div>
  );
};

export default RushForgotPasswordPage;
